import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { FUNCTION_BY_VALUE } from '../../../lib/pbisConstants';
import {
  useUserProfiles,
  shortRoleLabel,
  roleColorClasses,
} from 'context/UserProfilesContext';
import { deriveNameFromEmail } from 'lib/currentUser';

const IncidentTimeline = ({ incidents = [], selectedStudent, onIncidentSelect }) => {
  const [filterType, setFilterType] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterFunction, setFilterFunction] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const { getUserById } = useUserProfiles();

  const behaviorTypeFilters = [
    { value: 'all', label: 'All Behaviors' },
    { value: 'verbal-aggression', label: 'Verbal Aggression' },
    { value: 'physical-aggression', label: 'Physical Aggression' },
    { value: 'non-compliance', label: 'Non-Compliance' },
    { value: 'disruption', label: 'Disruption' },
    { value: 'elopement', label: 'Elopement' },
    { value: 'self-injury', label: 'Self-Injury' },
  ];

  const severityFilters = [
    { value: 'all', label: 'All Severities' },
    { value: 'low', label: 'Low (1-2)' },
    { value: 'medium', label: 'Medium (3)' },
    { value: 'high', label: 'High (4-5)' },
  ];

  const functionFilters = [
    { value: 'all', label: 'All Functions' },
    { value: 'attention', label: 'Attention' },
    { value: 'escape', label: 'Escape / Avoid' },
    { value: 'tangible', label: 'Tangible' },
    { value: 'sensory', label: 'Sensory' },
    { value: 'automatic', label: 'Automatic' },
    { value: 'unknown', label: 'Unknown' },
  ];

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'severity', label: 'By Severity' },
  ];

  // Support both new (occurredAt) and legacy (timestamp) shapes.
  const timeOf = (i) => new Date(i?.occurredAt || i?.timestamp || 0);

  const filteredIncidents = (incidents || [])
    .filter((incident) => {
      if (filterType !== 'all' && incident?.behaviorType !== filterType) return false;
      if (filterSeverity !== 'all') {
        const severity = incident?.intensity ?? 0;
        if (filterSeverity === 'low' && severity > 2) return false;
        if (filterSeverity === 'medium' && severity !== 3) return false;
        if (filterSeverity === 'high' && severity < 4) return false;
      }
      if (filterFunction !== 'all' && incident?.hypothesizedFunction !== filterFunction) return false;
      return true;
    })
    .sort((a, b) => {
      switch (sortOrder) {
        case 'oldest':
          return timeOf(a) - timeOf(b);
        case 'severity':
          return (b?.intensity ?? 0) - (a?.intensity ?? 0);
        default:
          return timeOf(b) - timeOf(a);
      }
    });

  const getBehaviorIcon = (behaviorType) => {
    const iconMap = {
      'verbal-aggression': 'MessageSquare',
      'physical-aggression': 'Zap',
      'non-compliance': 'X',
      disruption: 'AlertTriangle',
      elopement: 'ArrowRight',
      'self-injury': 'Heart',
      'property-destruction': 'Trash2',
      'inappropriate-language': 'MessageCircle',
      stereotypy: 'RotateCw',
      crying: 'CloudRain',
      withdrawal: 'MinusCircle',
      other: 'HelpCircle',
    };
    return iconMap[behaviorType] || 'Circle';
  };

  const getSeverityColor = (intensity) => {
    if (!intensity) return 'text-muted-foreground bg-muted';
    if (intensity <= 2) return 'text-success bg-success/10';
    if (intensity === 3) return 'text-warning bg-warning/10';
    return 'text-destructive bg-destructive/10';
  };

  const getSeverityLabel = (intensity) => {
    if (!intensity) return '—';
    if (intensity <= 2) return 'Low';
    if (intensity === 3) return 'Medium';
    return 'High';
  };

  const formatDateTime = (incident) => {
    const date = timeOf(incident);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
    };
  };

  const formatBehaviorType = (type) =>
    (type || '')
      .split('-')
      .map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1))
      .join(' ');

  const formatDuration = (incident) => {
    if (incident?.durationSeconds != null) {
      const s = Math.max(0, Number(incident.durationSeconds) || 0);
      return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
    }
    if (incident?.duration) return incident.duration;
    return null;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-card-foreground">Recent Incidents</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {selectedStudent?.firstName
              ? `${selectedStudent.firstName} ${selectedStudent.lastName ?? ''}`.trim()
              : selectedStudent?.name || 'Selected student'}{' '}
            — {filteredIncidents.length} incidents
          </p>
        </div>
        <Button variant="outline" size="sm">
          <Icon name="Download" size={16} />
          Export
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Select label="Behavior" options={behaviorTypeFilters} value={filterType} onChange={setFilterType} />
        <Select label="Severity" options={severityFilters} value={filterSeverity} onChange={setFilterSeverity} />
        <Select label="Function" options={functionFilters} value={filterFunction} onChange={setFilterFunction} />
        <Select label="Sort" options={sortOptions} value={sortOrder} onChange={setSortOrder} />
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {filteredIncidents.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No incidents match the current filters</p>
          </div>
        ) : (
          filteredIncidents.map((incident, index) => {
            const { date, time } = formatDateTime(incident);
            const duration = formatDuration(incident);
            const fn = incident?.hypothesizedFunction
              ? FUNCTION_BY_VALUE[incident.hypothesizedFunction]
              : null;
            const reporter = getUserById(incident?.reportedBy);
            const reporterName =
              reporter?.displayName ||
              deriveNameFromEmail(reporter?.email) ||
              (incident?.reportedBy ? 'A teammate' : null);
            const reporterShort = shortRoleLabel(reporter);
            const reporterColor = roleColorClasses(reporter);
            const isLast = index === filteredIncidents.length - 1;

            return (
              <div key={incident?.id} className="relative">
                {!isLast && <div className="absolute left-6 top-12 w-0.5 h-full bg-border" />}
                <div
                  className="flex gap-4 p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors duration-200 cursor-pointer"
                  onClick={() => onIncidentSelect?.(incident)}
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${getSeverityColor(
                      incident?.intensity
                    )}`}
                  >
                    <Icon name={getBehaviorIcon(incident?.behaviorType)} size={20} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-medium text-card-foreground">
                          {formatBehaviorType(incident?.behaviorType)}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>
                            {date} at {time}
                          </span>
                          {incident?.location && (
                            <>
                              <span>•</span>
                              <span className="capitalize">{incident.location.replace(/-/g, ' ')}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {fn && (
                          <span
                            className="px-2 py-1 rounded-full text-xs font-medium border"
                            style={{ borderColor: fn.color, color: fn.color, backgroundColor: `${fn.color}12` }}
                          >
                            {fn.label}
                          </span>
                        )}
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(incident?.intensity)}`}
                        >
                          {getSeverityLabel(incident?.intensity)}
                        </span>
                        {incident?.followUpRequired && (
                          <Icon name="Flag" size={16} className="text-warning" title="Follow-up required" />
                        )}
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                      {incident?.behaviorDescription}
                    </p>

                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {duration && (
                        <div className="flex items-center gap-1">
                          <Icon name="Clock" size={12} />
                          <span>{duration}</span>
                        </div>
                      )}
                      {incident?.interventionUsed && (
                        <div className="flex items-center gap-1">
                          <Icon name="Shield" size={12} />
                          <span className="capitalize">
                            {String(incident.interventionUsed).replace(/-/g, ' ')}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3 text-xs">
                      {incident?.antecedent && (
                        <div className="bg-muted/50 rounded px-2 py-1">
                          <span className="font-medium">A:</span>{' '}
                          <span className="capitalize">{String(incident.antecedent).replace(/-/g, ' ')}</span>
                        </div>
                      )}
                      {incident?.consequence && (
                        <div className="bg-muted/50 rounded px-2 py-1">
                          <span className="font-medium">C:</span>{' '}
                          <span className="capitalize">{String(incident.consequence).replace(/-/g, ' ')}</span>
                        </div>
                      )}
                    </div>

                    {reporterName && (
                      <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                        <Icon name="User" size={12} />
                        <span>Reported by {reporterName}</span>
                        {reporterShort && (
                          <span
                            className={`inline-block text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${reporterColor}`}
                          >
                            {reporterShort}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {filteredIncidents.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-card-foreground">{filteredIncidents.length}</p>
              <p className="text-sm text-muted-foreground">Total Incidents</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-warning">
                {(
                  Math.round(
                    (filteredIncidents.reduce((sum, inc) => sum + (inc?.intensity ?? 0), 0) /
                      Math.max(1, filteredIncidents.length)) *
                      10
                  ) / 10
                ).toFixed(1)}
              </p>
              <p className="text-sm text-muted-foreground">Avg. Intensity</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-destructive">
                {filteredIncidents.filter((inc) => inc?.followUpRequired).length}
              </p>
              <p className="text-sm text-muted-foreground">Need Follow-up</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentTimeline;
